import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Single shared source of "needs attention" counts used by the Sidebar badges,
// the Topbar bell, and the Dashboard summary. Counts follow the currently
// selected personal/team CRM instead of aggregating the whole company.
const AttentionContext = createContext({
  counts: { leads: 0, tasks: 0, renewals: 0, notifications: 0 }, refresh: () => {},
});

export const useAttention = () => useContext(AttentionContext);

export function AttentionProvider({ children }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ leads: 0, tasks: 0, renewals: 0, notifications: 0 });
  const timer = useRef();

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const scopeId = localStorage.getItem("cc_crm_scope") || user.id;
      const { data } = await api.get("/team/attention-counts", { params: { user_id: scopeId } });
      setCounts(data);
    } catch { /* keep last known counts on transient errors */ }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    refresh();
    timer.current = setInterval(refresh, 45000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer.current); document.removeEventListener("visibilitychange", onVis); };
  }, [user, refresh]);

  return (
    <AttentionContext.Provider value={{ counts, refresh }}>{children}</AttentionContext.Provider>
  );
}
